import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  OnInit,
  Optional,
  Self,
  ViewChild,
  AfterViewInit,
  Renderer2,
  Input,
  SimpleChanges,
  OnChanges,
  EventEmitter,
  Output,
  ViewEncapsulation,
  ElementRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
  MatAutocompleteSelectedEvent,
  MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
} from '@angular/material/autocomplete';
import { Overlay } from '@angular/cdk/overlay';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { map, Observable, startWith } from 'rxjs';
import { SearchItem } from './models/search.interface';
// import { MatOptionSelectionChange } from '@angular/material/core';
import { ScrollingModule } from '@angular/cdk/scrolling';

@Component({
  selector: 'app-buscador-autocomplete',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
    ScrollingModule,
    MatIconModule,
  ],
  templateUrl: './buscador-autocomplete.component.html',
  styleUrl: './buscador-autocomplete.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.close(),
    },
  ],
})
export class BuscadorAutocompleteComponent
  implements OnInit, ControlValueAccessor, AfterViewInit, OnChanges
{
  @ViewChild(MatAutocompleteTrigger) autoTrigger: MatAutocompleteTrigger;
  @Input() data: SearchItem[];
  @Output() selectedItem = new EventEmitter<SearchItem>();
  myControl = new FormControl();
  options: SearchItem[] = [];
  filteredOptions: Observable<SearchItem[]>;
  isDisabled = false;

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private elementRef: ElementRef
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      this.options = changes['data'].currentValue || [];
    }
  }

  ngOnInit() {
    // Inicializar options con data si está disponible
    if (this.data) {
      this.options = this.data;
    }

    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );
  }

  ngAfterViewInit(): void {
    this.setSearchBorder();
  }

  onChange: any = () => {};
  onTouch: any = () => {};

  writeValue(obj: any): void {
    this.myControl.setValue(obj);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.myControl.valueChanges.subscribe(fn); // Propaga los cambios al padre
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.myControl.disable() : this.myControl.enable();
  }

  private _filter(value: string | SearchItem): SearchItem[] {
    const term = typeof value === 'string' ? value : (value?.name ?? '');
    const filterValue = term.toLowerCase();

    return (
      this.options?.filter(option =>
        option.name.toLowerCase().includes(filterValue)
      ) || []
    );
  }

  displayFn = (value?: string | SearchItem): string => {
    if (!value) return '';
    return typeof value === 'string' ? value : value.name;
  };

  setSearchBorder() {
    const inputArea = this.elementRef.nativeElement.querySelector(
      '.mdc-text-field--filled:not(.mdc-text-field--disabled)'
    );

    if (inputArea && this.autoTrigger) {
      this.autoTrigger.autocomplete.opened.subscribe(() => {
        inputArea.classList.add('panel-open');
      });
      this.autoTrigger.autocomplete.closed.subscribe(() => {
        inputArea.classList.remove('panel-open');
      });
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const option = event.option.value as SearchItem;
    // Emit the selected item to parent consumers
    this.selectedItem.emit(option);
    // Keep the text input with the selected name to preserve control type as string
    this.myControl.setValue(option?.name ?? '');
    // Ensure the overlay is fully closed and focus released (prevents Chrome glitches)
    try {
      this.autoTrigger.closePanel();
      (
        this.elementRef.nativeElement.querySelector('input') as HTMLInputElement
      )?.blur();
    } catch {}
  }

  onClick() {
    this.myControl.setValue('');
  }
}
